import { useRent } from '@services/contracts/queires/useRent'
import { useChangeStatus } from '@services/offers/mutations/useChangeStatus'
import { AlertTriangle } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RentModel } from 'types/api'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog'

const isFutureRent = (rent: RentModel) =>
  new Date(rent.due_date) >= new Date(new Date().toDateString())

const formatAmount = (value?: string) => `${Number(value ?? 0).toFixed(2)} €`

/**
 * Termination flow for an ONGOING offer: lists the offer's unpaid rents with
 * checkboxes and terminates via the change-status endpoint, deleting the
 * selected rents atomically on the backend. Future-dated rents (not yet owed)
 * start checked; overdue ones (real debt) start unchecked.
 */
export function TerminateContractDialog(props: { offerId: string }) {
  const { offerId } = props
  const { t } = useTranslation('offers')
  const { t: tc } = useTranslation('contract')
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const { data } = useRent({ id: offerId, limit: -1 })
  const changeStatus = useChangeStatus()

  const unpaidRents = (data?.results ?? []).filter(
    (rent): rent is RentModel & { id: string } => !rent.paid && !!rent.id
  )
  const allChecked =
    unpaidRents.length > 0 && unpaidRents.every((r) => selected.has(r.id))

  const openDialog = () => {
    setSelected(
      new Set(unpaidRents.filter(isFutureRent).map((rent) => rent.id))
    )
    setOpen(true)
  }

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const toggleAll = () =>
    setSelected(
      allChecked ? new Set() : new Set(unpaidRents.map((rent) => rent.id))
    )

  const terminate = () =>
    changeStatus.mutate(
      {
        id: offerId,
        body: { status: 'TERMINATED', delete_rent_ids: [...selected] }
      },
      { onSuccess: () => setOpen(false) }
    )

  return (
    <>
      <Button
        onClick={openDialog}
        className="bg-destructive text-white hover:bg-destructive-strong"
      >
        {t('admin.actions.terminate_contract')}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-destructive/10 text-destructive">
                <AlertTriangle className="h-5 w-5" />
              </span>
              <div>
                <DialogTitle>
                  {t('admin.actions.terminate_dialog_title')}
                </DialogTitle>
                <p className="m-0 mt-1 text-sm text-muted-foreground">
                  {unpaidRents.length > 0
                    ? t('admin.actions.terminate_unpaid_rents_hint')
                    : t('admin.actions.no_unpaid_rents')}
                </p>
              </div>
            </div>
          </DialogHeader>

          {unpaidRents.length > 0 && (
            <div className="max-h-72 overflow-y-auto rounded-xl border border-ink/[0.08]">
              <label className="flex cursor-pointer items-center gap-3 border-b border-ink/[0.06] bg-ink/[0.02] px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Checkbox checked={allChecked} onCheckedChange={toggleAll} />
                {t('admin.actions.select_all')}
              </label>
              {unpaidRents.map((rent) => (
                <label
                  key={rent.id}
                  className="flex cursor-pointer items-center gap-3 border-b border-ink/[0.06] px-4 py-2.5 last:border-b-0 hover:bg-ink/[0.02]"
                >
                  <Checkbox
                    checked={selected.has(rent.id)}
                    onCheckedChange={() => toggle(rent.id)}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-ink">
                      {tc(`rent_table.${rent.payment_type ?? 'RENT'}`)}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {tc('rent_table.due_date')}:{' '}
                      {new Date(rent.due_date).toLocaleDateString('sk-SK')}
                      {' · '}
                      {tc('rent_table.variable_symbol')}: {rent.variable_symbol}
                    </div>
                  </div>
                  <span className="shrink-0 text-sm font-semibold text-ink">
                    {formatAmount(rent.amount)}
                  </span>
                </label>
              ))}
            </div>
          )}

          <DialogFooter>
            <Button variant="ghostDark" onClick={() => setOpen(false)}>
              {t('admin.form.no')}
            </Button>
            <Button
              onClick={terminate}
              disabled={changeStatus.isPending}
              className="bg-destructive text-white hover:bg-destructive-strong"
            >
              {t('admin.actions.terminate_contract')}
              {selected.size > 0 && ` (${selected.size})`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
